// Demo data for the AI components. Every brand, person, serial and number here is fictional
// (it belongs to the rotating hero units in ../heroUnits). Copy rules: plain, no em-dashes, no hype.
import { intelligence } from '../content';
import { heroUnits, type HeroUnit } from '../heroUnits';

export type Tone = 'light' | 'dark';
export const defaultUnit = heroUnits[0];
export const FALLBACK_PHOTO = '/images/v2/gen/heatpump-commission-b.jpg';

export type UnitQA = {
  q: string; // the owner's question
  short: string; // the same question, shorter, for small screens
  a: string; // grounded answer
  cite: string; // "Installation manual p.42"
  cover: string; // "Warranty active until Mar 2028"
  action: string; // the one suggested next step
  generic: string; // what a general-purpose chatbot says without the unit
  followUp: string; // owner, when the fix did not work
  teamReply: string; // what a person on the brand's team sends after taking over
  learned: string; // the resolution, as a draft answer for this model
  history: string; // one line of service history the record holds
};

const series = (u: HeroUnit) => u.serial.split('-')[0];

const QA: Record<HeroUnit['key'], Omit<UnitQA, 'cite' | 'cover'> & { cite?: string; cover?: string }> = {
  heatpump: {
    q: intelligence.sample.q,
    short: 'E21 and the fan has stopped?',
    a: intelligence.sample.a,
    cite: intelligence.sample.source.split(' · ')[0],
    cover: intelligence.sample.source.split(' · ')[1],
    action: 'Book Brightwell Heating',
    generic:
      'E21 can mean different things on different heat pumps. It is often a sensor, fan or refrigerant fault. Check the manufacturer\'s manual or contact a qualified heating engineer.',
    followUp: 'Did the defrost. It came back after an hour.',
    teamReply: 'Thanks for trying that. I have booked Brightwell Heating for Thursday morning to fit a new fan motor, under warranty. You will get a confirmation shortly.',
    learned: 'E21 returning after defrost: replace fan motor assembly under warranty.',
    history: 'Commissioned by Brightwell Heating, Mar 2026',
  },
  mower: {
    q: 'First cut of spring and it will not start. Is it broken?',
    short: 'Will not start?',
    a: 'Probably not. On the Moorcroft RM-42 the engine only cranks with someone in the seat, so check the seat safety switch first, then the battery. After a winter in the shed the starter battery is often flat; charge it or fit a new one.',
    action: 'Order a starter battery from Fenwick',
    generic:
      'A mower that will not start can have many causes, including the battery, fuel, spark plug or a safety switch. Check the owner\'s manual or contact a mower service centre.',
    followUp: 'New battery fitted. It starts, but the deck belt slips.',
    teamReply: 'Thanks. That points to the deck drive belt. Fenwick Garden Machinery can fit a new one under warranty, with the spring service kit, on Tuesday.',
    learned: 'Starts after battery, deck belt slips: replace deck drive belt under warranty.',
    history: 'Pre-delivery inspection by Fenwick Garden Machinery, Mar 2026',
  },
  oven: {
    q: 'Steam is weak this morning. What should I check?',
    short: 'Weak steam?',
    a: 'On the Fornell CX-10 weak steam is usually scale in the steam generator. Descale the steam generator every 200 hours in hard-water areas. Yours was last descaled 260 hours ago.',
    action: 'Book Northline Catering Services',
    generic:
      'Weak steam in a commercial oven can be caused by scale, a faulty valve or low water pressure. Refer to the manufacturer\'s documentation or contact a service engineer.',
    followUp: 'Descaled it. Steam is still weak.',
    teamReply: 'Thanks, that narrows it down to the element. Northline Catering Services will bring a new steam generator element tomorrow at 6am, before your first bake.',
    learned: 'Weak steam after descaling: replace steam generator element.',
    history: 'Installed by Northline Catering Services, Feb 2026',
  },
  ebike: {
    q: 'The range has dropped a lot this year. Is the battery failing?',
    short: 'Range dropping?',
    a: 'Some loss is normal. On the Velora T8 range drops in cold weather and as the battery ages. Store the battery indoors, charge to 80% for daily rides, and ask Canal Street Cycles for a battery health check to see how much capacity it still holds.',
    action: 'Book a battery health check',
    generic:
      'E-bike range depends on temperature, tyre pressure, assistance level and battery age. Check the manufacturer\'s guidance or ask a bike shop to test the battery.',
    followUp: 'Health check done. One cell group is out of balance.',
    teamReply: 'Thanks. That is covered by your battery warranty. Canal Street Cycles will rebalance or replace the pack this week.',
    learned: 'Range loss with a cell group out of balance at health check: battery warranty route via dealer.',
    history: 'Set up by Canal Street Cycles, Mar 2026',
  },
  lift: {
    q: 'The platform has stopped at height. How do I bring it down?',
    short: 'Stuck at height?',
    a: 'On the Talbrook TX12, use the emergency lowering valve at the rear of the chassis. Pull and hold it until the platform is fully down. Then take the lift out of use and report the defect.',
    action: 'Report a defect to Pennine',
    generic:
      'Most scissor lifts have an emergency lowering system, but where it is and how it works depends on the model. Check the operator manual or contact the hire company.',
    followUp: 'It is down now. The pothole guard did not deploy.',
    teamReply: 'Thanks. Keep it out of use. Pennine Lift Services will fit a new pothole guard tomorrow morning, under warranty, and check it before it goes back on hire.',
    learned: 'Stopped at height, pothole guard not deploying: replace the pothole guard under warranty.',
    history: 'Pre-delivery inspection by Pennine Lift Services, Mar 2024',
  },
  battery: {
    q: 'The power went off in the storm and the battery did not take over. Why?',
    short: 'No backup in a power cut?',
    a: 'On the Aurel H13 backup only runs when a reserve is set, and yours is at 0%. Set a 20% reserve in Settings, then test it with the switch on the gateway.',
    action: 'Set a backup reserve',
    generic: 'Home batteries handle power cuts differently. Some need a backup gateway or a reserve setting. Check the manufacturer\'s manual or contact your installer.',
    followUp: 'Set the reserve to 20%. The backup test still fails.',
    teamReply: 'Thanks for trying that. The gateway relay needs replacing. Northgate Solar will fit a new one on Friday morning, under warranty.',
    learned: 'Backup test fails with a reserve set: replace the gateway relay under warranty.',
    history: 'Commissioned by Northgate Solar, Apr 2026',
  },
  robotmower: {
    q: 'It keeps stopping by the hedge with a lost signal message. What do I do?',
    short: 'Lost signal?',
    a: 'On the Verdo R7 a tall hedge can block the satellite signal. Move the boundary 50 cm away from it in the lawn map, or turn on edge assist for that zone.',
    action: 'Edit the lawn map',
    generic: 'Robot mowers can lose signal near trees, walls and buildings. Check the manufacturer\'s guide or contact your dealer.',
    followUp: 'Moved the line. Now it stalls on the slope by the shed.',
    teamReply: 'Thanks. That points to the drive motor. Hollins Garden Centre will swap it under warranty on Saturday and bring new blades.',
    learned: 'Stalls on slopes after a map fix: replace the drive motor under warranty.',
    history: 'Installed by Hollins Garden Centre, Apr 2026',
  },
  icemaker: {
    q: 'The ice is cloudy and small tonight. What should I check?',
    short: 'Cloudy ice?',
    a: 'On the Nordis IM-150 that is usually a spent water filter or scale. Yours was changed seven months ago, past the six-month interval. Change the filter and run a clean cycle.',
    action: 'Order a water filter',
    generic: 'Cloudy ice can be caused by water quality, scale or a dirty machine. Check the manufacturer\'s manual or call a refrigeration engineer.',
    followUp: 'New filter and a clean. Still small cubes.',
    teamReply: 'Thanks, that points to the inlet valve. Coldwell Refrigeration will fit a new one tomorrow at 10am, before you open.',
    learned: 'Small cubes after filter and clean: replace the inlet valve.',
    history: 'Installed by Coldwell Refrigeration, Apr 2026',
  },
  coffee: {
    q: 'Shots are running fast and sour this morning. Is it the machine?',
    short: 'Sour shots?',
    a: 'Probably the grind. On the Corvino E2, go two steps finer and check the brew pressure reads 9 bar. Yours reads 9.1, so the machine is fine.',
    action: 'Open the dial-in guide',
    generic: 'Sour espresso is usually under-extraction: grind size, dose or water temperature. Check your machine\'s manual or ask a coffee technician.',
    followUp: 'Ground finer. Now the steam wand drips all the time.',
    teamReply: 'Thanks. That is the steam valve seal. Tamperline Coffee Services will fit a new one tomorrow at 7am, before you open, under warranty.',
    learned: 'Steam wand drips: replace the steam valve seal under warranty.',
    history: 'Installed by Tamperline Coffee Services, Apr 2026',
  },
  hottub: {
    q: 'The water has gone cloudy after the weekend. What do I do?',
    short: 'Cloudy water?',
    a: 'On the Calder S6 that is normal after heavy use. Shock the water, rinse both filters and keep chlorine at 3 to 5 ppm. Your filters are due for a change in two weeks.',
    action: 'Order a filter pair',
    generic: 'Cloudy hot tub water can come from water balance, filters or sanitiser levels. Check the manufacturer\'s guide or ask a pool and spa shop.',
    followUp: 'Shocked it and rinsed the filters. Now the heater shows FL1.',
    teamReply: 'Thanks. FL1 means low flow to the heater. Westbrook Spas will check the circulation pump on Wednesday, under warranty.',
    learned: 'FL1 after a filter clean: check the circulation pump under warranty.',
    history: 'Delivered by Westbrook Spas, May 2026',
  },
};

export function qaFor(u: HeroUnit = defaultUnit): UnitQA {
  const d = QA[u.key];
  return { ...d, cite: d.cite ?? u.source, cover: d.cover ?? 'Warranty active' };
}

export const seriesOf = series;

// Setup steps, materials and other small facts the lifecycle vignettes show for each unit.
export const unitLife: Record<HeroUnit['key'], { setup: string[]; tip: string; materials: string[]; takeBack: string; serviceDue: string; recycleNote: string }> = {
  heatpump: {
    setup: ['Pair the controller', 'Set your heating schedule', 'Choose the flow temperature'],
    tip: 'Running it steadily costs less than switching it on and off.',
    materials: ['Steel casing', 'Copper and aluminium coil', 'Refrigerant: recover, never vent'],
    takeBack: 'Free collection through your installer',
    serviceDue: 'Annual service due in 3 weeks',
    recycleNote: 'Material data from your ERP, on the same code. Heat pumps have no passport date yet.',
  },
  mower: {
    setup: ['Check oil and fuel', 'Test the seat safety switch', 'Show the owner the cutting heights'],
    tip: 'Charge the battery over winter so it starts on the first cut of spring.',
    materials: ['Steel deck and chassis', 'Engine: drain oil and fuel first', 'Starter battery: separate route'],
    takeBack: 'Part exchange or take-back through your dealer',
    serviceDue: 'Spring service due: blades and service kit',
    recycleNote: 'From 20 January 2027 the Machinery Regulation lets instructions be digital. The same code carries them, and the material data from your ERP.',
  },
  oven: {
    setup: ['Check the power and water supply', 'Calibrate the core probe', 'Train the kitchen team'],
    tip: 'Preheat for ten minutes after a cold start before the first load.',
    materials: ['Stainless steel', 'Mineral wool insulation', 'Glass door'],
    takeBack: 'Refurbishment or take-back through your service partner',
    serviceDue: 'Steam system service due in 3 weeks',
    recycleNote: 'Material data from your ERP, on the same code. Professional ovens have no passport rules.',
  },
  ebike: {
    setup: ['Record the frame number', 'Register the battery', 'Fit the bike to the rider'],
    tip: 'Charge to 80% for daily rides and store the battery indoors in winter.',
    materials: ['Lithium-ion battery: battery recycling route', 'Aluminium frame', 'Motor and electronics: WEEE route'],
    takeBack: 'Battery take-back through your dealer, with the data the recycler needs',
    serviceDue: 'Battery health check due',
    recycleNote: 'Batteries for light means of transport, such as e-bikes, need a battery passport from 18 February 2027. The same code carries it.',
  },
  lift: {
    setup: ['Add it to the hire fleet', 'Set the six-monthly examination dates', 'Add the pre-use check form'],
    tip: 'Do the pre-use check at the start of every shift and log it on the page.',
    materials: ['Steel chassis and scissor pack', 'Hydraulic oil: drain first', 'Traction batteries: separate route'],
    takeBack: 'Refurbishment or take-back through your service partner',
    serviceDue: 'LOLER thorough examination due in 3 weeks',
    recycleNote: 'From 20 January 2027 the Machinery Regulation lets instructions be digital. The same code carries them, and the material data from your ERP.',
  },
  battery: {
    setup: ['Check the backup circuits', 'Set the charging schedule', 'Choose a backup reserve'],
    tip: 'Charge on the overnight rate and use it at teatime. That saves the most.',
    materials: ['Lithium iron phosphate cells', 'Aluminium enclosure', 'Inverter: WEEE route'],
    takeBack: 'Take-back through your installer, with the data the recycler needs',
    serviceDue: 'Annual health check due in 3 weeks',
    recycleNote: 'Home batteries over 2 kWh need a battery passport from 18 February 2027. The same code carries it.',
  },
  robotmower: {
    setup: ['Place the base station', 'Draw the lawn map', 'Set the mowing schedule'],
    tip: 'Mow little and often. Three short cuts a week keep the lawn thicker than one long one.',
    materials: ['Lithium-ion battery', 'Recyclable plastic body', 'Motors: WEEE route'],
    takeBack: 'Take-back through your dealer',
    serviceDue: 'Winter service due: blades and cleaning',
    recycleNote: 'From 20 January 2027 the Machinery Regulation lets instructions be digital. The same code carries them, with the battery data recyclers need.',
  },
  icemaker: {
    setup: ['Fit the water filter', 'Check the drain', 'Run the first harvest'],
    tip: 'Keep the bin door shut between scoops. It melts less and makes more.',
    materials: ['Stainless steel', 'Refrigerant: recover first', 'Compressor: WEEE route'],
    takeBack: 'Refurbishment or take-back through your service partner',
    serviceDue: 'Six-month clean due in 2 weeks',
    recycleNote: 'Material and refrigerant data from your ERP, on the same code, ready for when passport rules arrive.',
  },
  coffee: {
    setup: ['Fit the water filter', 'Dial in the grinder', 'Train the baristas'],
    tip: 'Backflush every evening. It keeps every shot tasting the same.',
    materials: ['Stainless steel and brass', 'Copper boiler', 'Pump and controls: WEEE route'],
    takeBack: 'Refurbishment or take-back through your service partner',
    serviceDue: 'Six-month service due in 3 weeks',
    recycleNote: 'Material data from your ERP, on the same code, ready for when passport rules arrive.',
  },
  hottub: {
    setup: ['Fill and balance the water', 'Set the heating schedule', 'Show the owner the filters'],
    tip: 'Keep the cover on between uses. It saves most of the heating cost.',
    materials: ['Acrylic shell', 'Wood-effect cladding', 'Pumps and heater: WEEE route'],
    takeBack: 'Take-back through your dealer',
    serviceDue: 'Filter change due in 2 weeks',
    recycleNote: 'Material data from your ERP, on the same code, ready for when passport rules arrive.',
  },
};
